import { Controller } from "@hotwired/stimulus"
import { formatTime } from "../lib/format_time"

// Public playlist player: transport controls around a native <audio> element.
// Track title/artist/cover come from "track-metadata:loaded" events dispatched
// by the track-metadata controller mounted on each tracklist row.
export default class extends Controller {
  static targets = [
    "audioElement", "playPauseIcon", "scrubber",
    "trackCard", "nowPlayingTitle", "nowPlayingArtist", "nowPlayingCover",
    "currentTime", "duration", "volumeSlider"
  ]
  static values = { tracks: Array }

  connect() {
    this.currentIndex = null
    this.isSeeking = false
    this.metadataCache = {}

    this.audioElementTarget.addEventListener("timeupdate", this.syncScrubberBound = this.syncScrubber.bind(this))
    this.audioElementTarget.addEventListener("loadedmetadata", this.syncDurationBound = this.syncDuration.bind(this))
    this.audioElementTarget.addEventListener("ended", this.nextBound = this.next.bind(this))
    this.audioElementTarget.addEventListener("play", this.onPlayBound = () => this.setPlayingIcon(true))
    this.audioElementTarget.addEventListener("pause", this.onPauseBound = () => this.setPlayingIcon(false))
    this.element.addEventListener("track-metadata:loaded", this.cacheMetadataBound = this.cacheMetadata.bind(this))

    if (this.hasVolumeSliderTarget) this.audioElementTarget.volume = this.volumeSliderTarget.value / 100
  }

  disconnect() {
    this.audioElementTarget.pause()
    this.audioElementTarget.removeEventListener("timeupdate", this.syncScrubberBound)
    this.audioElementTarget.removeEventListener("loadedmetadata", this.syncDurationBound)
    this.audioElementTarget.removeEventListener("ended", this.nextBound)
    this.audioElementTarget.removeEventListener("play", this.onPlayBound)
    this.audioElementTarget.removeEventListener("pause", this.onPauseBound)
    this.element.removeEventListener("track-metadata:loaded", this.cacheMetadataBound)
  }

  cacheMetadata(event) {
    const index = Number(event.target.dataset.trackIndex)
    if (Number.isNaN(index)) return

    this.metadataCache[index] = event.detail.tags
    if (index === this.currentIndex) this.updateNowPlaying(event.detail.tags, this.tracksValue[index].filename)
  }

  playTrack(event) {
    const index = Number(event.currentTarget.dataset.trackIndex)
    this.loadTrack(index)
    this.audioElementTarget.play()
  }

  loadTrack(index) {
    this.currentIndex = index
    const track = this.tracksValue[index]
    this.audioElementTarget.src = track.url

    this.trackCardTargets.forEach((card, i) => {
      card.classList.toggle("active", i === index)
    })

    this.updateNowPlaying(this.metadataCache[index] || {}, track.filename)
  }

  updateNowPlaying(tags, fallbackTitle = "") {
    this.nowPlayingTitleTarget.textContent = tags.title || fallbackTitle
    this.nowPlayingArtistTarget.textContent = tags.artist || ""

    if (tags.picture) {
      const { data, format } = tags.picture
      let binary = ""
      for (let i = 0; i < data.length; i++) binary += String.fromCharCode(data[i])
      this.nowPlayingCoverTarget.src = `data:${format};base64,${window.btoa(binary)}`
      this.nowPlayingCoverTarget.classList.remove("d-none")
    } else {
      this.nowPlayingCoverTarget.classList.add("d-none")
    }
  }

  togglePlay() {
    if (this.currentIndex === null) this.loadTrack(0)

    if (this.audioElementTarget.paused) {
      this.audioElementTarget.play()
    } else {
      this.audioElementTarget.pause()
    }
  }

  setPlayingIcon(isPlaying) {
    this.playPauseIconTarget.classList.toggle("bi-play-fill", !isPlaying)
    this.playPauseIconTarget.classList.toggle("bi-pause-fill", isPlaying)
  }

  previous() {
    if (!this.tracksValue.length) return
    const start = this.currentIndex === null ? 0 : this.currentIndex
    this.loadTrack((start - 1 + this.tracksValue.length) % this.tracksValue.length)
    this.audioElementTarget.play()
  }

  next() {
    if (!this.tracksValue.length) return
    const start = this.currentIndex === null ? -1 : this.currentIndex
    this.loadTrack((start + 1) % this.tracksValue.length)
    this.audioElementTarget.play()
  }

  startSeeking() {
    this.isSeeking = true
  }

  stopSeeking() {
    this.isSeeking = false
  }

  seek(event) {
    if (!this.audioElementTarget.duration) return
    this.audioElementTarget.currentTime = (event.target.value / 100) * this.audioElementTarget.duration
  }

  syncScrubber() {
    if (this.hasCurrentTimeTarget) this.currentTimeTarget.textContent = formatTime(this.audioElementTarget.currentTime)
    if (this.isSeeking || !this.audioElementTarget.duration) return
    this.scrubberTarget.value = (this.audioElementTarget.currentTime / this.audioElementTarget.duration) * 100
  }

  syncDuration() {
    if (this.hasDurationTarget) this.durationTarget.textContent = formatTime(this.audioElementTarget.duration)
  }

  setVolume(event) {
    this.audioElementTarget.volume = event.target.value / 100
  }
}
